import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Stack,
    Grid,
    CircularProgress,
    Alert,
    Container,
    MenuItem,
    Divider,
} from "@mui/material";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import { getInviteDetails, registerUser } from "../api";

const GENDER_OPTS = ["Male", "Female", "Other"];

const KYC_DOC_FIELDS = [
    ["PAN", "pan"],
    ["Aadhaar", "aadhaar"],
    ["Cancelled cheque", "cheque"],
];

const isImageUrl = (url) => {
    if (!url) return false;
    const path = url.split("?")[0].toLowerCase();
    return /\.(jpe?g|png|webp)$/.test(path);
};

const ALLOWED_UPLOAD_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf",
];
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const thumbSx = {
    maxHeight: 120,
    maxWidth: "100%",
    objectFit: "contain",
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    display: "block",
};

function KycUploadSlot({ label, existingUrl, upload, onSelect, onRemove }) {
    const [imgError, setImgError] = useState(false);

    const uploadControl = (text) => (
        <Button
            size="small"
            variant="outlined"
            component="label"
            sx={{ mt: 1 }}
        >
            {text}
            <input
                hidden
                type="file"
                accept="image/*,application/pdf"
                onChange={onSelect}
            />
        </Button>
    );

    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
            >
                {label}
            </Typography>
            {upload ? (
                <Box>
                    {upload.isImage ? (
                        <Box
                            component="img"
                            src={upload.dataUrl}
                            alt={label}
                            sx={thumbSx}
                        />
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{ wordBreak: "break-all" }}
                        >
                            {upload.name}
                        </Typography>
                    )}
                    <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                        <Button size="small" variant="outlined" component="label">
                            Replace
                            <input
                                hidden
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={onSelect}
                            />
                        </Button>
                        <Button size="small" color="error" onClick={onRemove}>
                            Remove
                        </Button>
                    </Stack>
                </Box>
            ) : existingUrl ? (
                <Box>
                    {isImageUrl(existingUrl) && !imgError ? (
                        <Box
                            component="img"
                            src={existingUrl}
                            alt={label}
                            onError={() => setImgError(true)}
                            onClick={() =>
                                window.open(
                                    existingUrl,
                                    "_blank",
                                    "noopener,noreferrer",
                                )
                            }
                            sx={{ ...thumbSx, cursor: "pointer" }}
                        />
                    ) : (
                        <Button
                            size="small"
                            variant="outlined"
                            href={existingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View current document
                        </Button>
                    )}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                    >
                        Provided by inviter
                    </Typography>
                    {uploadControl("Replace")}
                </Box>
            ) : (
                uploadControl("Upload")
            )}
        </Box>
    );
}

const initialForm = {
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    gender: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    dateOfBirth: "",
    panCardNumber: "",
    aadharNumber: "",
    nameOnAadhar: "",
    bankAccountNumber: "",
    ifscCode: "",
    mpin: "",
    confirmMpin: "",
};

export default function Register() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token");
    const [invite, setInvite] = useState(null);
    const [kycDocs, setKycDocs] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [uploads, setUploads] = useState({
        pan: null,
        aadhaar: null,
        cheque: null,
    });
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");

    useEffect(() => {
        if (!token) {
            setError("Invalid invite link");
            setLoading(false);
            return;
        }
        getInviteDetails(token)
            .then(({ data }) => {
                // API returns invite object directly (snake_case)
                const inv = data || {};
                setInvite(inv);
                setKycDocs(inv.kycDocuments || null);
                setRejectionReason(
                    inv.rejection_reason ?? inv.rejectionReason ?? "",
                );
                const dob = inv.date_of_birth ?? inv.dateOfBirth;
                const dobStr =
                    dob instanceof Date
                        ? dob.toISOString().slice(0, 10)
                        : typeof dob === "string"
                          ? dob.slice(0, 10)
                          : "";
                setForm((f) => ({
                    ...f,
                    firstName: inv.first_name ?? inv.firstName ?? "",
                    lastName: inv.last_name ?? inv.lastName ?? "",
                    email: inv.email ?? "",
                    mobileNumber: inv.mobile_number ?? inv.mobileNumber ?? "",
                    gender: inv.gender ?? "",
                    dateOfBirth: dobStr,
                    address1: inv.address_1 ?? inv.address1 ?? "",
                    address2: inv.address_2 ?? inv.address2 ?? "",
                    city: inv.city ?? "",
                    state: inv.state ?? "",
                    pincode: inv.pincode ?? "",
                    panCardNumber:
                        inv.pan_card_number ?? inv.panCardNumber ?? "",
                    aadharNumber:
                        inv.aadhar_card_number ?? inv.aadharNumber ?? "",
                    nameOnAadhar: inv.name_on_aadhar ?? inv.nameOnAadhar ?? "",
                    bankAccountNumber:
                        inv.bank_account_number ?? inv.bankAccountNumber ?? "",
                    ifscCode: inv.ifsc_code ?? inv.ifscCode ?? "",
                }));
            })
            .catch((e) =>
                setError(
                    e.response?.data?.error || "Invalid or expired invite link",
                ),
            )
            .finally(() => setLoading(false));
    }, [token]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const isKycSatisfied = (key) =>
        !!uploads[key] || !!(kycDocs && kycDocs[key]);
    const allKycSatisfied = KYC_DOC_FIELDS.every(([, key]) =>
        isKycSatisfied(key),
    );

    const handleFileSelect = (key) => async (e) => {
        setError("");
        const file = e.target.files?.[0];
        // Reset so selecting the same file again still fires onChange.
        e.target.value = "";
        if (!file) return;
        if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
            return setError(
                "Only JPG, PNG, WEBP or PDF files are allowed",
            );
        }
        if (file.size > MAX_UPLOAD_SIZE) {
            return setError("File must be 5MB or smaller");
        }
        try {
            const dataUrl = await readFileAsDataUrl(file);
            setUploads((u) => ({
                ...u,
                [key]: {
                    dataUrl,
                    name: file.name,
                    isImage: file.type !== "application/pdf",
                },
            }));
        } catch {
            setError("Could not read the selected file");
        }
    };

    const removeUpload = (key) => () =>
        setUploads((u) => ({ ...u, [key]: null }));

    const handleSubmit = async () => {
        setError("");
        if (
            !form.firstName ||
            !form.lastName ||
            !form.mobileNumber ||
            !form.mpin
        ) {
            return setError("Please fill all required fields");
        }
        if (form.mpin.length < 4 || form.mpin.length > 6)
            return setError("MPIN must be 4-6 digits");
        if (form.mpin !== form.confirmMpin)
            return setError("MPIN does not match");
        if (!allKycSatisfied) {
            return setError(
                "Please provide all KYC documents (PAN, Aadhaar, Cancelled cheque)",
            );
        }
        setSubmitting(true);
        try {
            const { confirmMpin: _, ...body } = form;
            const kycDocuments = {};
            KYC_DOC_FIELDS.forEach(([, key]) => {
                if (uploads[key]) kycDocuments[key] = uploads[key].dataUrl;
            });
            const { data } = await registerUser({
                ...body,
                inviteToken: token,
                kycDocuments,
            });
            setSuccessMsg(data?.message || "");
            setSuccess(true);
        } catch (e) {
            setError(e.response?.data?.error || "Registration failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading)
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg,#1c6b8c,#a855f7)",
                }}
            >
                <CircularProgress sx={{ color: "#fff" }} />
            </Box>
        );

    if (success)
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg,#1c6b8c,#a855f7)",
                }}
            >
                <Card
                    sx={{
                        p: 5,
                        textAlign: "center",
                        maxWidth: 420,
                        borderRadius: 4,
                    }}
                >
                    <HourglassTopRoundedIcon
                        sx={{ fontSize: 64, color: "warning.main", mb: 2 }}
                    />
                    <Typography variant="h5" fontWeight={700} mb={1}>
                        Account Pending Approval
                    </Typography>
                    <Typography color="text.secondary" mb={3}>
                        {successMsg ||
                            "Your account has been created and is pending admin approval. You'll be able to sign in once an administrator approves your account."}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/signin")}
                        sx={{ borderRadius: 3, px: 4 }}
                    >
                        Back to Sign In
                    </Button>
                </Card>
            </Box>
        );

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: 4,
                background: "linear-gradient(135deg,#1c6b8c,#a855f7)",
            }}
        >
            <Container maxWidth="md">
                <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        gap={1.5}
                        mb={1}
                    >
                        <Box
                            component="img"
                            src="/logo-icon.png"
                            alt="BiznessPay"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                            sx={{
                                height: 64,
                                width: 64,
                                objectFit: "contain",
                                bgcolor: "#fff",
                                borderRadius: 2,
                                p: 0.5,
                            }}
                        />
                        <Typography
                            variant="h5"
                            sx={{ color: "#fff", fontWeight: 800 }}
                        >
                            BiznessPay
                        </Typography>
                    </Stack>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                        Complete your registration
                    </Typography>
                    {invite && (
                        <Typography
                            sx={{
                                color: "rgba(255,255,255,0.5)",
                                mt: 0.5,
                                fontSize: 13,
                            }}
                        >
                            Invited as{" "}
                            <strong style={{ color: "rgba(255,255,255,0.8)" }}>
                                {(
                                    invite.role_name ??
                                    invite.roleName ??
                                    ""
                                ).replace(/_/g, " ")}
                            </strong>
                            {(invite.inviter_name ?? invite.inviterName)
                                ? ` by ${invite.inviter_name ?? invite.inviterName}`
                                : ""}
                        </Typography>
                    )}
                </Box>

                <Card sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
                    {rejectionReason && (
                        <Alert
                            severity="warning"
                            sx={{ mb: 2, borderRadius: 2 }}
                        >
                            Your previous documents were not approved:{" "}
                            {rejectionReason}. Please upload corrected documents
                            below.
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        mb={2}
                        color="primary"
                    >
                        Personal Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="First Name *"
                                value={form.firstName}
                                onChange={set("firstName")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Last Name *"
                                value={form.lastName}
                                onChange={set("lastName")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                value={form.email}
                                onChange={set("email")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Mobile Number *"
                                value={form.mobileNumber}
                                onChange={set("mobileNumber")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Gender"
                                value={form.gender}
                                onChange={set("gender")}
                            >
                                {GENDER_OPTS.map((g) => (
                                    <MenuItem key={g} value={g}>
                                        {g}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Date of Birth"
                                type="date"
                                value={form.dateOfBirth}
                                onChange={set("dateOfBirth")}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        mb={2}
                        color="primary"
                    >
                        Address
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Line 1"
                                value={form.address1}
                                onChange={set("address1")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Line 2"
                                value={form.address2}
                                onChange={set("address2")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="City"
                                value={form.city}
                                onChange={set("city")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="State"
                                value={form.state}
                                onChange={set("state")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Pincode"
                                value={form.pincode}
                                onChange={set("pincode")}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        mb={2}
                        color="primary"
                    >
                        KYC Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="PAN Card Number"
                                value={form.panCardNumber}
                                onChange={set("panCardNumber")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Aadhar Number"
                                value={form.aadharNumber}
                                onChange={set("aadharNumber")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Name on Aadhar"
                                value={form.nameOnAadhar}
                                onChange={set("nameOnAadhar")}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        mb={0.5}
                        color="primary"
                    >
                        KYC Documents
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 2 }}
                    >
                        All three documents are required (JPG, PNG, WEBP or PDF,
                        max 5MB each).
                    </Typography>
                    <Grid container spacing={2}>
                        {KYC_DOC_FIELDS.map(([label, key]) => (
                            <Grid item xs={12} sm={4} key={key}>
                                <KycUploadSlot
                                    label={label}
                                    existingUrl={kycDocs ? kycDocs[key] : null}
                                    upload={uploads[key]}
                                    onSelect={handleFileSelect(key)}
                                    onRemove={removeUpload(key)}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        mb={2}
                        color="primary"
                    >
                        Bank Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Bank Account Number"
                                value={form.bankAccountNumber}
                                onChange={set("bankAccountNumber")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="IFSC Code"
                                value={form.ifscCode}
                                onChange={set("ifscCode")}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        mb={2}
                        color="primary"
                    >
                        Set Your MPIN
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="MPIN (4-6 digits) *"
                                type="password"
                                value={form.mpin}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        mpin: e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 6),
                                    })
                                }
                                inputProps={{ maxLength: 6 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Confirm MPIN *"
                                type="password"
                                value={form.confirmMpin}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        confirmMpin: e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 6),
                                    })
                                }
                                inputProps={{ maxLength: 6 }}
                            />
                        </Grid>
                    </Grid>

                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleSubmit}
                        disabled={submitting || !allKycSatisfied}
                        sx={{ mt: 4, py: 1.5, borderRadius: 3, fontSize: 16 }}
                    >
                        {submitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Create Account"
                        )}
                    </Button>
                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            mt: 2,
                            textAlign: "center",
                            color: "text.secondary",
                        }}
                    >
                        Invite links expire after 72 hours. If your link has
                        expired, ask your inviter to send a new one.
                    </Typography>
                </Card>
            </Container>
        </Box>
    );
}
